<template>
  <div class="puzzle-hint-panel">
    <!-- Collapsed trigger -->
    <div v-if="!expanded" class="hint-trigger" @click="toggleExpand()">
      <i class="tim-icons icon-send mr-2"></i>
      Interaction options
      <span v-if="hintsSentCount > 0" class="hints-sent-badge">({{ hintsSentCount }} sent)</span>
      <i class="tim-icons icon-minimal-down ml-auto"></i>
    </div>

    <!-- Expanded panel -->
    <div v-if="expanded" class="hint-expanded">
      <div class="hint-expanded-header" @click="toggleExpand()">
        <span class="hint-expanded-label">Interaction options</span>
        <span v-if="hintsSentCount > 0" class="hints-sent-badge">({{ hintsSentCount }} sent)</span>
        <i class="tim-icons icon-minimal-up ml-auto" style="cursor: pointer"></i>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="text-center mt-3 mb-3">
        <i class="fas fa-spinner fa-spin"></i>
      </div>

      <!-- Message list -->
      <div v-if="!loading" class="mt-2">
        <div v-if="allClues.length === 0" class="hint-empty">
          No interactions have been configured.
        </div>
        <div v-for="clue in allClues" :key="'clue-' + clue._type + '-' + clue.id"
             class="hint-message-row">
          <div class="hint-message-content">
            <div class="d-flex align-items-center">
              <span class="hint-type-badge" :class="'hint-type-badge--' + clue._type">{{ clue._type }}</span>
              <span v-if="clue._type === 'text' && clue.text" class="ml-2 hint-message-text">{{ clue.text }}</span>
              <span v-if="clue._type !== 'text' && clue.file_url" class="ml-2 hint-message-text">{{ getFileName(clue.file_url) }}</span>
            </div>
            <img v-if="clue._type === 'image' && clue.file_url"
                 :src="clue.file_url" class="hint-message-thumbnail mt-1" />
            <small v-if="clue.description" class="d-block" style="color: #9a9a9a;">
              {{ clue.description }}
            </small>
          </div>
          <div class="hint-message-actions">
            <base-button
              v-if="clue._type === 'video'"
              @click.native="sendHint(clue, 'video', 1)"
              type="primary"
              size="sm"
              class="hint-send-btn mr-1"
            >Loop</base-button>
            <base-button
              @click.native="sendHint(clue, clue._type)"
              type="primary"
              size="sm"
              class="hint-send-btn"
              :class="{ 'hint-sent-success': sentClueId === clue.id }"
            >
              <span v-if="sentClueId !== clue.id">Send</span>
              <i v-else class="tim-icons icon-check-2"></i>
            </base-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import axios from "axios";

export default {
  name: "PuzzleHintPanel",
  props: {
    puzzleId: { type: Number, required: true },
    puzzleName: { type: String, default: '' },
    gameId: { type: Number, required: true },
    expanded: { type: Boolean, default: false },
    hintsSentCount: { type: Number, default: 0 }
  },
  data() {
    return {
      allClues: [],
      loading: false,
      loaded: false,
      sentClueId: null
    }
  },
  watch: {
    expanded: {
      handler(val) {
        if (val && !this.loaded) {
          this.loadAllClues()
        }
      },
      immediate: true
    }
  },
  methods: {
    toggleExpand() {
      this.$emit('update:expanded', !this.expanded)
    },
    getAuthToken() {
      return new Promise(function (resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async loadAllClues() {
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let baseUrl = API_LOCATION + 'companies/' + companyId + '/games/' + this.gameId
        + '/puzzles/' + this.puzzleId + '/clues?type='

      try {
        const types = ['text', 'image', 'audio', 'video']
        const results = await Promise.all(
          types.map(type => axios.get(baseUrl + type, config))
        )

        this.allClues = []
        types.forEach((type, i) => {
          const clues = results[i].data.clues || []
          clues.forEach(clue => {
            this.allClues.push({ ...clue, _type: type })
          })
        })
        this.loaded = true
      } catch (err) {
        console.log(err)
        if (err.response && err.response.status === 401) {
          this.$notify({
            type: 'danger',
            message: 'You do not have access to view interactions.',
            icon: 'tim-icons icon-alert-circle-exc'
          })
        }
      }
      this.loading = false
    },
    getFileName(file_url) {
      if (!file_url) return ''
      let parts = file_url.split('/')
      return parts[parts.length - 1]
    },
    sendHint(clue, type, loop) {
      let content = ''
      if (type === 'text') {
        content = clue.text
      } else {
        content = clue.file_url
      }

      this.$emit('send-hint', {
        content: content,
        type: type,
        clueId: clue.id,
        countAsHint: clue.count_as_hint,
        puzzleName: this.puzzleName,
        loop: loop || 0
      })

      // Show success feedback
      this.sentClueId = clue.id
      setTimeout(() => {
        this.sentClueId = null
      }, 1500)
    }
  }
}
</script>

<style scoped>
.puzzle-hint-panel {
  margin-top: 12px;
}

.hint-trigger {
  display: flex;
  align-items: center;
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  padding: 6px 12px;
  color: #9a9a9a;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hint-trigger:hover {
  border-color: #e14eca;
  color: #e14eca;
}

.hints-sent-badge {
  color: #e14eca;
  font-size: 11px;
  margin-left: 6px;
}

.hint-expanded {
  background: rgba(225, 78, 202, 0.03);
  border: 1px solid rgba(225, 78, 202, 0.12);
  border-radius: 6px;
  padding: 12px 16px;
}

.hint-expanded-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 8px;
}

.hint-expanded-label {
  font-size: 11px;
  letter-spacing: 0.5px;
  color: #9a9a9a;
  text-transform: uppercase;
}

/* ========================================
   Message list (flat, like ObjectiveDetailPanel)
   ======================================== */
.hint-message-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.hint-message-row:last-child {
  border-bottom: none;
}

.hint-message-content {
  flex: 1;
  min-width: 0;
  margin-right: 10px;
}

.hint-message-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.hint-message-thumbnail {
  width: 80px;
  height: 50px;
  border-radius: 4px;
  object-fit: cover;
}

.hint-message-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.hint-type-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

.hint-type-badge--text { color: #1d8cf8; background: rgba(29, 140, 248, 0.1); }
.hint-type-badge--image { color: #e14eca; background: rgba(225, 78, 202, 0.1); }
.hint-type-badge--audio { color: #ff8d72; background: rgba(255, 141, 114, 0.1); }
.hint-type-badge--video { color: #00f2c3; background: rgba(0, 242, 195, 0.1); }

.hint-send-btn {
  min-width: 55px;
  flex-shrink: 0;
}

.hint-sent-success {
  background: #00bf9a !important;
  border-color: #00bf9a !important;
}

.hint-empty {
  color: #9a9a9a;
  font-size: 12px;
  font-style: italic;
  padding: 12px 0;
}
</style>

<style>
.white-content .hint-message-text {
  color: #344675;
}

.white-content .hint-message-row {
  border-bottom-color: rgba(0, 0, 0, 0.06);
}

.white-content .hint-expanded {
  background: rgba(225, 78, 202, 0.03);
  border-color: rgba(225, 78, 202, 0.1);
}
</style>
